import { ArrowRightIcon, LockIcon, MessageCircleIcon, PinIcon, PlusIcon, SearchIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { formatDateDistance } from '@campai/lib-common/i18n'
import { markdownToPlainText } from '@campai/lib-common/utils/misc'
import { Actor, Paginator } from '@campai/lib-web/components'
import {
    Badge,
    Button,
    Card,
    CardContent,
    CardHeader,
    Item,
    ItemActions,
    ItemContent,
    ItemDescription,
    ItemGroup,
    ItemMedia,
    ItemTitle,
    Link,
    Skeleton,
} from '@campai/lib-web/components/new/ui'
import { type RouterOutput } from '@campai/lib-web/utils/trpc'

import { pageSize, type Search } from '@/product/community/category/routeUtils'

type TopicListItem = RouterOutput['product']['community']['topics']['listTopics']['topics'][number]

type TopicListProps = {
    topics: TopicListItem[] | null
    topicCount: number | null
    submittedSearchTerm: string
    canCreateTopic: boolean
    setTopicDialogOpen: (open: boolean) => void
    categoryId: string
    getListSearch: (overrides?: Partial<Search>) => Search
}

export const TopicList = observer(
    ({
        topics,
        topicCount,
        submittedSearchTerm,
        canCreateTopic,
        setTopicDialogOpen,
        categoryId,
        getListSearch,
    }: TopicListProps) => {
        const { i18n, t } = useTranslation()

        return (
            <div className="space-y-3">
                {!topics &&
                    Array.from({ length: 3 }).map((_, index) => (
                        <Card key={`community-topic-skeleton-${index.toString()}`}>
                            <CardHeader>
                                <Skeleton className="h-5 w-1/2" />
                                <Skeleton className="h-4 w-3/4" />
                            </CardHeader>
                        </Card>
                    ))}
                {topics?.length === 0 && topicCount === 0 && (
                    <Card>
                        <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
                            {submittedSearchTerm ? (
                                <SearchIcon className="text-muted-foreground size-8" />
                            ) : (
                                <MessageCircleIcon className="text-muted-foreground size-8" />
                            )}
                            <div className="font-medium">
                                {submittedSearchTerm
                                    ? t('product.community.category.searchEmpty.title')
                                    : t('product.community.category.empty.title')}
                            </div>
                            <div className="text-muted-foreground max-w-md text-sm">
                                {submittedSearchTerm
                                    ? t('product.community.category.searchEmpty.description')
                                    : t('product.community.category.empty.description')}
                            </div>
                            {canCreateTopic && !submittedSearchTerm && (
                                <Button onClick={() => setTopicDialogOpen(true)}>
                                    <PlusIcon data-icon="inline-start" />
                                    {t('product.community.category.createTopic')}
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                )}
                {!!topics?.length && (
                    <ItemGroup className="gap-3">
                        {topics.map((topic) => {
                            const createdAt = topic.createdAt
                                ? formatDateDistance(topic.createdAt, { i18n, addSuffix: true, isDateOnly: false })
                                : ''
                            const latestActivityAt = formatDateDistance(topic.latestActivityAt, {
                                i18n,
                                addSuffix: true,
                                isDateOnly: false,
                            })
                            const preview = markdownToPlainText(topic.message).replace(/\s+/g, ' ').trim()

                            return (
                                <Item
                                    key={topic._id.toString()}
                                    variant="outline"
                                    render={
                                        <Link
                                            variant="unstyled"
                                            to="/product/community/categories/$categoryId/topics/$topicId"
                                            params={{ categoryId, topicId: topic._id.toString() }}
                                            search={getListSearch()}
                                            active={false}
                                        />
                                    }
                                    className="bg-background data-[locked=true]:bg-muted/40 data-[read-state=new]:border-primary/70 data-[read-state=updated]:border-primary/60 data-[read-state=new]:bg-primary/10 data-[read-state=updated]:bg-primary/5 data-[read-state=new]:ring-primary/15 data-[read-state=updated]:ring-primary/10 group items-start gap-4 rounded-lg data-[read-state=new]:shadow-sm data-[read-state=updated]:shadow-sm data-[read-state=new]:ring-1 data-[read-state=updated]:ring-1"
                                    data-locked={!!topic.lockedAt}
                                    data-read-state={topic.readState}
                                >
                                    <ItemMedia className="!translate-y-0 !self-center">
                                        <Actor size="xl" actor={topic.createdBy!} />
                                    </ItemMedia>
                                    <ItemContent className="min-w-0 gap-1">
                                        <ItemTitle className="block w-full max-w-full truncate text-base leading-5">
                                            <span className="inline-flex max-w-full items-center gap-2">
                                                {!!topic.pinnedAt && (
                                                    <PinIcon className="text-muted-foreground size-4 shrink-0" />
                                                )}
                                                {!!topic.lockedAt && (
                                                    <LockIcon className="text-muted-foreground size-4 shrink-0" />
                                                )}
                                                <span
                                                    className="min-w-0 truncate data-[read-state=new]:font-bold data-[read-state=read]:font-normal data-[read-state=updated]:font-bold"
                                                    data-read-state={topic.readState}
                                                >
                                                    {topic.title}
                                                </span>
                                                {topic.readState === 'new' && (
                                                    <Badge color="blue" className="shrink-0">
                                                        {t('product.community.category.readState.new')}
                                                    </Badge>
                                                )}
                                            </span>
                                        </ItemTitle>
                                        <ItemDescription className="block w-full max-w-full truncate leading-4">
                                            {preview}
                                        </ItemDescription>
                                        <div className="mt-1.5 truncate text-xs leading-4">
                                            {t('product.community.category.createdAt', { date: createdAt })}
                                            {topic.postsCount > 0 && (
                                                <>
                                                    {' · '}
                                                    {t('product.community.category.lastReplyAt', {
                                                        date: latestActivityAt,
                                                    })}
                                                    {' · '}
                                                    {t('product.community.category.topicCounts', {
                                                        posts: topic.postsCount,
                                                    })}
                                                </>
                                            )}
                                            {!!topic.lockedAt && (
                                                <>
                                                    {' · '}
                                                    <Badge variant="outline" className="align-middle">
                                                        {t('product.community.category.locked')}
                                                    </Badge>
                                                </>
                                            )}
                                        </div>
                                    </ItemContent>
                                    <ItemActions className="ml-auto hidden shrink-0 items-center justify-end self-center sm:flex">
                                        <ArrowRightIcon className="text-muted-foreground size-5 shrink-0 transition-transform group-hover:translate-x-0.5" />
                                    </ItemActions>
                                </Item>
                            )
                        })}
                    </ItemGroup>
                )}
                {topicCount !== null && topicCount > pageSize && (
                    <Paginator
                        variant="footer"
                        to="/product/community/categories/$categoryId"
                        params={{ categoryId }}
                        pageSize={pageSize}
                        count={topicCount}
                    />
                )}
            </div>
        )
    }
)
